// The reply for a step started from Other Actions (2026-10-27,
// docs/contact-view.md "Other Actions: the same step"). Once the step is
// processed, its reply template is offered on the step: its name and a
// preview, filled the way a proposed step's reply is, and one button,
// PASTE_REPLY_LABEL, that pastes it into the draft reply (asking first when
// the box already holds a reply, 2026-10-30). It never replaces the reply by
// itself and never sends.
//
// Where the template comes from:
//   - a customer-paid Website return that comes back: the check's custPays
//     plan (the server plans it with the pipeline's own planCustPaysReply()
//     for the person's reason and items): one RMARTN Cust Pays reply, or,
//     when CXHub can't tell whether we carry the correct part, the Yes / No
//     choice between "Do Carry" (with the part link) and "Don't Carry";
//   - every other result: the template the step's Process resolved
//     (result.replyText, named by result.replyTemplate: Refund No Return,
//     Label Company Pays, Replace ...).
// Pure; tested in ui/test/manualStepReply.test.js.
import { fillReferenceIdAtSend, setRefundAmountLines, fillCarryTokens, CARRY_YES, CARRY_NO } from '@cxhub/shared'

// The one button label, in every case (2026-10-30, Jenn).
export const PASTE_REPLY_LABEL = 'Paste this into the draft reply - you still get to review before you click Send'

const needsLinkToken = (text) => /INSERTLINK(?![A-Z0-9])/.test(String(text ?? ''))

// [{ key, title, template, label, text, needsLink }] for the processed step,
// or []. title: the template's name (null only when nothing names it);
// template: the reply box's template tag ({ folder, title }) once pasted.
export function manualReplyOptions({ check = null, result = null } = {}) {
  const plan = check?.custPays ?? null
  const tag = (title, folder = null) => (title ? { folder: folder ?? null, title } : null)
  if (plan?.draft) {
    const title = plan.title ?? null
    return [{ key: 'cust_pays', title, template: tag(title, plan.folder), label: PASTE_REPLY_LABEL, text: plan.draft, needsLink: needsLinkToken(plan.draft) }]
  }
  const templates = (plan?.steps ?? []).filter((s) => s?.type === 'task_reply_template' && typeof s.params?.text === 'string' && s.params.text.trim())
  if (templates.length) {
    return templates.map((s) => {
      const title = s.params?.templateTitle ?? null
      const yes = s.taskPath === CARRY_YES
      return {
        key: s.taskPath ?? title ?? 'template',
        title,
        template: tag(title, s.params?.templateFolder),
        // Which answer it is, for the Carry choice (the name says it too).
        choice: yes ? 'carry_yes' : s.taskPath === CARRY_NO ? 'carry_no' : null,
        label: PASTE_REPLY_LABEL,
        text: s.params.text,
        needsLink: yes || needsLinkToken(s.params.text),
      }
    })
  }
  if (typeof result?.replyText === 'string' && result.replyText.trim()) {
    const t = result.replyTemplate
    const title = t && typeof t.title === 'string' && t.title ? t.title : null
    return [{ key: 'result', title, template: tag(title, t?.folder), label: PASTE_REPLY_LABEL, text: result.replyText, needsLink: false }]
  }
  return []
}

// The step's refund amounts, as the main panel keeps an RMARTN reply's
// lines in step with the return step: { original, deduction, final }.
export function manualReplyAmounts(inputs = {}) {
  const original = Number(inputs?.itemsTotal)
  if (inputs?.itemsTotal == null || !Number.isFinite(original)) return null
  const option = inputs?.shippingPaymentOption ?? 'customerCost'
  const raw = option === 'customerShipsOwn' ? 0 : inputs?.shippingDeduction
  const deduction = raw == null || raw === '' || !Number.isFinite(Number(raw)) ? null : Number(raw)
  return { original, deduction, final: deduction == null ? null : Math.round((original - deduction) * 100) / 100 }
}

// The option's text, filled: the RMA number as its Reference ID (the
// step's result), the refund lines from the step, and the part link and
// warranty when given. A value not known leaves its token, so Send's
// placeholder check still stops it.
export function manualReplyText(text, { erpRmaId = null, amounts = null, link = null, warranty = null } = {}) {
  let out = String(text ?? '')
  if (erpRmaId) out = fillReferenceIdAtSend(out, String(erpRmaId)).text
  if (amounts) out = setRefundAmountLines(out, amounts)
  if (link || warranty) out = fillCarryTokens(out, { link, warranty })
  return out
}

// The preview under the template's name: the first `lines` non-blank
// lines, and whether there is more ("Show all"). text: the reply as plain
// text, filled exactly as it would be pasted.
export function manualReplyPreview(text, lines = 4) {
  const all = String(text ?? '').replace(/\r\n/g, '\n').split('\n')
  while (all.length && !all[all.length - 1].trim()) all.pop()
  while (all.length && !all[0].trim()) all.shift()
  let seen = 0
  let cut = all.length
  for (let k = 0; k < all.length; k += 1) {
    if (all[k].trim()) seen += 1
    if (seen === lines) { cut = k + 1; break }
  }
  return { short: all.slice(0, cut).join('\n'), full: all.join('\n'), more: cut < all.length }
}

// "Pasted into the draft reply at <time>".
export function pastedNote(at) {
  const d = at instanceof Date ? at : new Date(at)
  const time = Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  return time ? `Pasted into the draft reply at ${time}` : 'Pasted into the draft reply'
}

// What the step did, in the customer's words for the reminder:
// 'replacement', 'return' or 'refund'.
export function untoldKind(action, result = null) {
  const type = action?.type ?? ''
  if (type === 'process_replacement') return 'replacement'
  if (type === 'refund_freight_trq' || type === 'refund_tax_trq') return 'refund'
  if (type === 'resend_return_label') return 'return'
  if (type === 'process_return_refund') {
    const route = String(result?.route ?? '')
    return /scrap|refund_no_return|task_refund/.test(route) ? 'refund' : 'return'
  }
  return 'step'
}

// The steps started from Other Actions whose customer hasn't been told
// (2026-10-30): processed in this session (processedAt, the server's time
// when the response carries it) with a reply to offer, and no reply sent
// since (an outbound message on the contact at or after that time). Pasting
// alone doesn't tell the customer (Jenn, 2026-10-30): a pasted step stays,
// marked pasted (pasted[index], or the box's template tag is that reply's
// template). [{ index, kind, options, pasted }].
export function untoldManualSteps({ actions = [], stepStates = {}, checks = {}, pasted = {}, boxTemplateTitle = null, messages = [] } = {}) {
  const out = []
  ;(actions ?? []).forEach((a, i) => {
    if (a?.source !== 'manual_panel') return
    const st = stepStates?.[i]
    if (st?.status !== 'success' || !st.processedAt) return
    const options = manualReplyOptions({ check: checks?.[i] ?? a.params?.checkResult ?? null, result: st.result })
    if (!options.length) return
    const after = new Date(st.processedAt).getTime()
    const sent = (messages ?? []).some((m) => {
      if (m?.direction !== 'outbound') return false
      const t = new Date(m.receivedAt ?? m.createdAt ?? 0).getTime()
      return Number.isFinite(t) && t >= after
    })
    if (sent) return
    const isPasted = Boolean(pasted?.[i]) || Boolean(boxTemplateTitle && options.some((o) => o.title && o.title === boxTemplateTitle))
    out.push({ index: i, kind: untoldKind(a, st.result), options, pasted: isPasted })
  })
  return out
}

const kindList = (steps) => {
  const kinds = [...new Set((steps ?? []).map((s) => s.kind).filter((k) => k && k !== 'step'))]
  return kinds.length === 1 ? kinds[0] : kinds.length ? `${kinds.slice(0, -1).join(', ')} and ${kinds[kinds.length - 1]}` : null
}

// The reminder before Close / Dismiss / leaving the contact (2026-10-30).
// Two cases: a reply never pasted ("Paste the reply"), or every reply pasted
// but not sent ("Send it": the reply box and Send get the focus, nothing is
// sent). Both have "Close anyway" and "Stay on this contact" (the cancel
// answer, as are Escape and a click outside). altLabel resolves 'alt'.
// mode: 'unpasted' | 'pasted'.
export function untoldReminderPrompt(steps = []) {
  const what = kindList(steps)
  const allPasted = (steps ?? []).length > 0 && steps.every((s) => s.pasted)
  if (allPasted) {
    return {
      mode: 'pasted',
      title: "The reply hasn't been sent yet",
      body: `The ${what ? `${what} ` : ''}reply is in the box but hasn't been sent yet.`,
      cancelLabel: 'Stay on this contact',
      altLabel: 'Send it',
      confirmLabel: 'Close anyway',
      testId: 'confirm-untold-customer',
    }
  }
  return {
    mode: 'unpasted',
    title: "The customer hasn't been told yet",
    body: `The customer hasn't been told about ${what ? `the ${what}` : 'this step'} yet. Paste the reply and send it, or close anyway.`,
    cancelLabel: 'Stay on this contact',
    altLabel: 'Paste the reply',
    confirmLabel: 'Close anyway',
    testId: 'confirm-untold-customer',
  }
}
